import { ChevronLeft, Loader2, RotateCw, TriangleAlert } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { Badge } from '@/shared/components/ui/badge'
import { CHAT_COPY, useBroadcast, type BroadcastDetail } from '@/entities/chat'
import { formatRelativeDateTime } from '@/shared/lib/DateUtil'

export function BroadcastRoster({ broadcastId, onBack }: { broadcastId: number; onBack: () => void }) {
  const { data, isLoading, error, refetch } = useBroadcast(broadcastId)

  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex items-center gap-2 border-b border-line px-3 py-2.5">
        <button
          type="button"
          onClick={onBack}
          aria-label="Back to broadcasts"
          className="shrink-0 rounded-md p-1 text-ink-3 hover:bg-hover hover:text-ink"
        >
          <ChevronLeft size={16} />
        </button>
        <p className="text-[13.5px] font-semibold text-ink">Broadcast</p>
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto p-3">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-5 w-5 animate-spin text-ink-4" />
          </div>
        ) : error || !data ? (
          <div className="flex flex-col items-center justify-center gap-2 py-16 text-center">
            <TriangleAlert className="h-6 w-6 text-bad" strokeWidth={1.6} />
            <p className="text-[13px] font-medium text-ink">{CHAT_COPY.listFailed}</p>
            <Button variant="outline" size="sm" className="mt-1" onClick={() => void refetch()}>
              <RotateCw className="h-3.5 w-3.5" />
              {CHAT_COPY.retry}
            </Button>
          </div>
        ) : (
          <RosterBody detail={data} />
        )}
      </div>
    </div>
  )
}

function RosterBody({ detail }: { detail: BroadcastDetail }) {
  const { broadcast, recipients } = detail
  const readRatio = broadcast.recipientCount > 0 ? Math.round((broadcast.readCount / broadcast.recipientCount) * 100) : 0

  return (
    <div className="space-y-4">
      <div className="bg-surface border border-line rounded-lg shadow-[var(--shadow-sm)] p-4 space-y-3">
        <p className="text-[12.5px] font-medium text-ink-3">{`Broadcast to ${broadcast.audienceName}`}</p>
        <p className="whitespace-pre-wrap wrap-anywhere text-[13.5px] text-ink">{broadcast.body}</p>
        <div className="flex items-center gap-2">
          <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-hover">
            <div className="h-full rounded-full bg-brand-accent" style={{ width: `${readRatio}%` }} />
          </div>
          <span className="shrink-0 text-[12px] text-ink-3">{`${broadcast.readCount} / ${broadcast.recipientCount} read`}</span>
        </div>
        <p className="text-[11.5px] text-ink-4">{`Sent by ${broadcast.sentByName}, ${formatRelativeDateTime(broadcast.sentOn)}`}</p>
      </div>

      <div className="bg-surface border border-line rounded-lg shadow-[var(--shadow-sm)] overflow-hidden">
        {recipients.length === 0 ? (
          <div className="flex items-center justify-center py-10 text-[13px] text-ink-3">{CHAT_COPY.rosterEmpty}</div>
        ) : (
          <div role="rowgroup">
            {recipients.map((recipient) => (
              <div
                key={recipient.userId}
                role="row"
                className="flex items-center justify-between gap-3 border-b border-line-2 px-4 py-2.5 last:border-0"
              >
                <span className="truncate text-[13px] text-ink">{recipient.name}</span>
                {recipient.readOn ? (
                  <span className="shrink-0 text-[12px] text-ink-3">{formatRelativeDateTime(recipient.readOn)}</span>
                ) : (
                  <Badge tone="warn">Unread</Badge>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
